'use client';

import { useMemo, useState } from 'react';
import { useT } from '@/i18n/client';
import { sample, shuffle, WORD_PAIRS, type WordPair } from '@/content/games';
import { playCorrect, playLose, playTap, playWin, playWrong } from '@/lib/sound';
import { Hearts } from './Hearts';

export interface GameFinish {
  correct: number;
  total: number;
  score: number;
}

const PAIRS = 6;
/** Wrong matches allowed before the round ends. */
const MAX_MISTAKES = 3;

interface Card {
  id: string;
  pairKey: string;
  text: string;
  lang: 'en' | 'rw';
}

/** Memory-style matching: pair each English word with its Kinyarwanda meaning. */
export function WordMatch({
  onFinish,
  pairs = WORD_PAIRS,
}: {
  onFinish: (result: GameFinish) => void;
  /** Optional word bank (e.g. a lesson's vocabulary); defaults to the global bank. */
  pairs?: WordPair[];
}) {
  const t = useT();
  const [round, setRound] = useState(0);
  const count = Math.min(PAIRS, pairs.length);
  const cards = useMemo<Card[]>(() => {
    const list: Card[] = [];
    sample(pairs, count).forEach((pair, i) => {
      list.push({ id: `e${i}`, pairKey: pair.en, text: pair.en, lang: 'en' });
      list.push({ id: `r${i}`, pairKey: pair.en, text: pair.rw, lang: 'rw' });
    });
    return shuffle(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round, pairs, count]);

  const [selected, setSelected] = useState<Card | null>(null);
  const [matched, setMatched] = useState<Set<string>>(new Set());
  const [wrongIds, setWrongIds] = useState<string[]>([]);
  const [moves, setMoves] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);

  function pick(card: Card) {
    if (done || matched.has(card.id) || wrongIds.length > 0) return;
    if (!selected) {
      playTap();
      setSelected(card);
      return;
    }
    if (selected.id === card.id) {
      setSelected(null);
      return;
    }
    const nextMoves = moves + 1;
    setMoves(nextMoves);
    if (selected.pairKey === card.pairKey && selected.lang !== card.lang) {
      const next = new Set(matched);
      next.add(selected.id);
      next.add(card.id);
      setMatched(next);
      setSelected(null);
      if (next.size === cards.length) {
        setDone(true);
        playWin();
        onFinish({ correct: count, total: count, score: Math.max(50, 600 - (nextMoves - count) * 25) });
      } else {
        playCorrect();
      }
    } else {
      const nextMistakes = mistakes + 1;
      setMistakes(nextMistakes);
      setWrongIds([selected.id, card.id]);
      setSelected(null);
      if (nextMistakes >= MAX_MISTAKES) {
        setDone(true);
        setFailed(true);
        playLose();
        // Score partial credit for the pairs matched before running out of hearts.
        const pairsMatched = matched.size / 2;
        onFinish({ correct: pairsMatched, total: count, score: Math.round(pairsMatched * 50) });
      } else {
        playWrong();
        window.setTimeout(() => setWrongIds([]), 650);
      }
    }
  }

  function playAgain() {
    setMatched(new Set());
    setSelected(null);
    setWrongIds([]);
    setMoves(0);
    setMistakes(0);
    setDone(false);
    setFailed(false);
    setRound((r) => r + 1);
  }

  return (
    <div className="vn-game2">
      <div className="vn-game2__topbar">
        <p className="vn-game2__status">{t('games.moves', { count: moves })}</p>
        <Hearts mistakes={mistakes} max={MAX_MISTAKES} />
      </div>
      <div className="vn-match__grid">
        {cards.map((card) => {
          const isMatched = matched.has(card.id);
          const isSel = selected?.id === card.id;
          const isWrong = wrongIds.includes(card.id);
          return (
            <button
              key={card.id}
              type="button"
              className={`vn-match__card vn-match__card--${card.lang}${isMatched ? ' is-matched' : ''}${
                isSel ? ' is-sel' : ''
              }${isWrong ? ' is-wrong' : ''}`}
              disabled={isMatched}
              onClick={() => pick(card)}
            >
              {card.text}
            </button>
          );
        })}
      </div>
      {done ? (
        <div className="vn-game2__result">
          <p className="vn-game2__result-title">
            {failed ? t('games.outOfHearts') : t('games.matchDone', { moves })}
          </p>
          <button type="button" className="vn-btn vn-btn--primary" onClick={playAgain}>
            {t('games.playAgain')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
